import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useSession } from '../auth/session.tsx';
import { useAssistantContext } from '../assistant/AssistantContext.tsx';
import { Thread } from '../assistant/Thread.tsx';
import { Composer } from '../assistant/Composer.tsx';
import { StarterChips } from '../assistant/StarterChips.tsx';
import '../assistant/assistant-s6.css';

/**
 * Docked assistant panel (docs/06 §assistant, behavior in docs/05). Wires the
 * SSE chat: streamed markdown, tool traces, inline confirmation cards, file
 * drop/paste, and voice. The conversation persists across toggles + navigation
 * via useAssistant (conversation_id in localStorage).
 */
export function AssistantPanel({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const { chat, attach, consumePendingAsk, askSignal } = useAssistantContext();
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);
  const threadRef = useRef<HTMLDivElement>(null);

  const firstName = me.user.name.split(' ')[0];
  const empty = chat.turns.length === 0 && !chat.historyLoading;

  // Auto-scroll to the newest content as the stream grows.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.turns, chat.streamError]);

  const handleSend = (text: string) => {
    chat.send({
      text,
      mediaIds: attach.mediaIds,
      attachments: attach.items
        .filter((a) => a.phase === 'done' && a.mediaId)
        .map((a) => ({ id: a.mediaId!, name: a.name })),
    });
    attach.reset();
  };

  // Drain a prompt queued by an "Ask assistant" deep-link once the panel is mounted (it only
  // mounts when the shell opens it — see AssistantContext.ask). Runs whenever a new ask fires.
  useEffect(() => {
    const prompt = consumePendingAsk();
    if (prompt && !chat.streaming) chat.send({ text: prompt, mediaIds: [], attachments: [] });
    // Intentionally keyed on askSignal only — it bumps once per queued prompt.
  }, [askSignal]);

  // Drag/drop onto the whole panel → stage as attachments.
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) attach.add(files);
  };
  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.files);
    if (files.length) {
      e.preventDefault();
      attach.add(files);
    }
  };

  return (
    <aside
      className={`assistant${dragOver ? ' drag-over' : ''}`}
      aria-label="Assistant"
      onDragEnter={(e) => {
        if (Array.from(e.dataTransfer.types).includes('Files')) {
          dragDepth.current += 1;
          setDragOver(true);
        }
      }}
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault();
      }}
      onDragLeave={() => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={onDrop}
      onPaste={onPaste}
    >
      <div className="assistant-head">
        <strong>Assistant</strong>
        <div className="row">
          <button
            className="btn ghost sm"
            onClick={chat.newConversation}
            title="Start a new conversation"
            disabled={chat.turns.length === 0}
          >
            New
          </button>
          <button className="close-x" aria-label="Close assistant" onClick={onClose}>
            ×
          </button>
        </div>
      </div>

      <div className="assistant-body" ref={threadRef}>
        {chat.historyLoading && <div className="text-dim">Loading conversation…</div>}
        {empty && (
          <div className="asst-welcome">
            <p>
              Hi {firstName} — I can build playlists and schedules from plain language. Drop a
              file and tell me where it should play.
            </p>
            <StarterChips onPick={handleSend} />
          </div>
        )}
        <Thread
          turns={chat.turns}
          streamError={chat.streamError}
          onConfirm={chat.confirm}
          onUndo={chat.undo}
          onRetry={chat.retryLast}
        />
      </div>

      {dragOver && (
        <div className="asst-dropzone" aria-hidden>
          Drop files to attach
        </div>
      )}

      <Composer
        attachments={attach.items}
        uploading={attach.uploading}
        streaming={chat.streaming}
        onAddFiles={attach.add}
        onRemoveAttachment={attach.remove}
        onSend={handleSend}
      />
    </aside>
  );
}
